import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getPreLeadNotes } from '../../services/api/implementation/impl';
import {
  formatPreLeadNoteDate,
  getPreLeadLegacyLog,
  getPreLeadLogAuthor,
  getPreLeadLogDate,
  getPreLeadLogText,
  getPreLeadReason,
  sortPreLeadLogsNewestFirst
} from '../../utils/preleadLogUtils';
import {
  PRELEAD_STATUS_CATEGORIES,
  PRELEAD_STATUS_CATEGORY
} from '../../utils/preleadStatusOptions';

function NoteHistoryEntry({ entry }) {
  const text = getPreLeadLogText(entry);
  const author = getPreLeadLogAuthor(entry);
  const dateLabel = formatPreLeadNoteDate(getPreLeadLogDate(entry));
  if (!text) return null;

  return (
    <div className="rounded-md border border-gray-200 bg-white px-2.5 py-2">
      <p className="whitespace-pre-wrap text-sm text-gray-900">{text}</p>
      {(author !== '—' || dateLabel) && (
        <p className="mt-1 text-[11px] text-gray-500">
          {[author !== '—' ? author : null, dateLabel].filter(Boolean).join(' · ')}
        </p>
      )}
    </div>
  );
}

/**
 * Update Status modal for preleads — categorized dispositions.
 * Notes are required only when status is "Others".
 * Shows existing activity-history notes so prior comments stay visible while updating.
 * onConfirm({ categoryKey, statusLabel, notes })
 */
const PreleadUpdateStatusModal = ({
  open,
  onClose,
  onConfirm,
  isSubmitting = false,
  prelead = null
}) => {
  const [categoryKey, setCategoryKey] = useState(PRELEAD_STATUS_CATEGORY.CONNECTED_QUALIFIED);
  const [statusLabel, setStatusLabel] = useState('');
  const [notes, setNotes] = useState('');

  const preleadId = prelead?.id ?? prelead?.pre_lead_id ?? null;

  const { data: notesData, isLoading: notesLoading } = useQuery({
    queryKey: ['preLeadNotes', preleadId],
    queryFn: () => getPreLeadNotes(preleadId),
    enabled: Boolean(open && preleadId)
  });

  const legacyLog = getPreLeadLegacyLog(notesData) || getPreLeadLegacyLog(prelead);
  const reason = getPreLeadReason(prelead);
  const historyNotes = sortPreLeadLogsNewestFirst(notesData?.notes ?? []);
  const hasHistory = Boolean(legacyLog || reason || historyNotes.length > 0);

  useEffect(() => {
    if (open) {
      setCategoryKey(PRELEAD_STATUS_CATEGORY.CONNECTED_QUALIFIED);
      setStatusLabel('');
      setNotes('');
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !isSubmitting) onClose?.();
    };
    if (open) {
      document.addEventListener('keydown', onKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose, isSubmitting]);

  if (!open) return null;

  const activeCategory =
    PRELEAD_STATUS_CATEGORIES.find((c) => c.key === categoryKey) ?? PRELEAD_STATUS_CATEGORIES[0];
  const notesRequired = statusLabel === 'Others';
  const canSave =
    Boolean(statusLabel.trim()) && (!notesRequired || Boolean(notes.trim())) && !isSubmitting;

  const handleCategoryChange = (nextKey) => {
    setCategoryKey(nextKey);
    setStatusLabel('');
  };

  const handleSave = async () => {
    const trimmedStatus = statusLabel.trim();
    const trimmedNotes = notes.trim();
    if (!trimmedStatus) {
      toast.error('Please select a status.');
      return;
    }
    if (notesRequired && !trimmedNotes) {
      toast.error('Please enter notes for Others.');
      return;
    }
    await onConfirm?.({
      categoryKey,
      statusLabel: trimmedStatus,
      notes: trimmedNotes
    });
  };

  return (
    <div
      className="fixed inset-0 z-[9100] flex items-center justify-center bg-black/30 px-4 py-8"
      onClick={isSubmitting ? undefined : onClose}
      role="presentation">
      <div
        className="flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="prelead-update-status-title">
        <div className="shrink-0 border-b border-customBlue/15 bg-[#E9F5F6] px-5 py-3">
          <h2 id="prelead-update-status-title" className="text-base font-semibold text-customBlue">
            Update Status
          </h2>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {preleadId ? (
            <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50/70 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                Activity history
              </p>
              {notesLoading ? (
                <p className="text-sm text-gray-500">Loading notes…</p>
              ) : hasHistory ? (
                <div className="max-h-40 space-y-2 overflow-y-auto pr-1">
                  {reason ? (
                    <div className="rounded-md border border-amber-200 bg-amber-50/60 px-2.5 py-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-800">
                        Reason
                      </p>
                      <p className="mt-1 whitespace-pre-wrap text-sm text-gray-900">{reason}</p>
                    </div>
                  ) : null}
                  {legacyLog ? (
                    <div className="rounded-md border border-amber-200 bg-amber-50/50 px-2.5 py-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-800">
                        Previous note
                      </p>
                      <p className="mt-1 whitespace-pre-wrap text-sm text-gray-900">{legacyLog}</p>
                    </div>
                  ) : null}
                  {historyNotes.map((entry) => (
                    <NoteHistoryEntry
                      key={entry.id ?? `${getPreLeadLogDate(entry)}-${getPreLeadLogText(entry)}`}
                      entry={entry}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No notes yet.</p>
              )}
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2">
            {PRELEAD_STATUS_CATEGORIES.map((cat) => {
              const selected = cat.key === categoryKey;
              return (
                <button
                  key={cat.key}
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => handleCategoryChange(cat.key)}
                  className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 ${
                    selected
                      ? 'border-customBlue bg-customBlue text-white'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}>
                  {cat.emoji} {cat.label}
                </button>
              );
            })}
          </div>

          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">
              {activeCategory.emoji} {activeCategory.label}
            </p>
            <div className="max-h-52 space-y-1 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50/60 p-2">
              {activeCategory.options.map((option) => {
                const selected = statusLabel === option;
                const optionId = `prelead-status-${categoryKey}-${option.replace(/\W+/g, '-')}`;
                return (
                  <label
                    key={option}
                    htmlFor={optionId}
                    className={`flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors ${
                      selected ? 'bg-white ring-1 ring-customBlue/40 text-customBlue' : 'hover:bg-white/80 text-gray-800'
                    }`}>
                    <input
                      id={optionId}
                      type="radio"
                      name="prelead-status-option"
                      value={option}
                      checked={selected}
                      disabled={isSubmitting}
                      onChange={() => setStatusLabel(option)}
                      className="h-3.5 w-3.5 border-gray-300 text-customBlue focus:ring-customBlue"
                    />
                    <span>{option}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {statusLabel ? (
            <div className="space-y-1.5">
              <label
                htmlFor="prelead-status-notes"
                className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                Add Notes
                {notesRequired ? <span className="text-red-600"> *</span> : null}
              </label>
              <textarea
                id="prelead-status-notes"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={
                  notesRequired
                    ? 'Enter remarks for Others…'
                    : 'Enter remarks for this status update (optional)…'
                }
                disabled={isSubmitting}
                className="w-full resize-y rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-customBlue focus:outline-none focus:ring-1 focus:ring-customBlue disabled:opacity-60"
              />
              <p className="text-xs text-gray-500">
                {notesRequired
                  ? 'Notes are required for Others and saved to activity history.'
                  : 'Notes are optional and saved to activity history when provided.'}
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-500">Select a status to save.</p>
          )}
        </div>

        <div className="flex shrink-0 justify-end gap-3 border-t border-gray-100 bg-gray-50/80 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="min-w-[6.5rem] rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="min-w-[6.5rem] rounded-lg bg-customBlue px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 disabled:opacity-50">
            {isSubmitting ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PreleadUpdateStatusModal;
